import React, {
  useMemo,
  useState,
  createContext,
  useEffect,
  lazy,
  Suspense,
} from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider, CssBaseline, useMediaQuery } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import GlobalBaseStyles from "./styles/GlobalStyles";
import "./styles/fonts.css";
import { buildTheme } from "./styles/theme";
// import AccountPage from './pages/ACCOUNT'; // TODO: Create ACCOUNT page
import { AuthProvider } from "./contexts/AuthContext";
import { GuestAuthProvider } from "./contexts/GuestAuthProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import PairingRoute from "./components/PairingRoute";
import LogoMotion from "./components/LogoMotion";
import CenterSpinnerMUI from "./components/CenterSpinner";
import {
  startUrlMonitoring,
  monitorUrlForAuthInfo,
} from "./utils/webViewUtils";
import StudioImportRuntime from "./components/StudioImportRuntime";
import ReportIssueHost from "./components/ReportIssueHost";
import { WebWorkerProvider } from "./contexts/WebWorkerContext";
import { AppToastProvider } from "./contexts/AppToastProvider";
import { PersistentToastProvider } from "./contexts/PersistentToastProvider";
import { PersistentToastRouteBoundary } from "./contexts/PersistentToastRouteBoundary";
import { useServerStateEventBridge } from "./hooks/queries";
import PwaUpdateCoordinator from "./components/PwaUpdateCoordinator";
import PwaDiagnostics from "./components/PwaDiagnostics";
import { requestPwaUpdate } from "./pwaUpdate";
import DesktopDeviceRoute from "./mobile/DesktopDeviceRoute";
import MobileDeviceRoute from "./mobile/MobileDeviceRoute";
import {
  getClientBuildId,
  publishBuildInfo,
  shouldRequestPwaUpdate,
} from "./buildVersion";
import { isGuestAuthEnabled } from "./utils/guestAuth";

// 로그인은 진입점이므로 lazy 제거 → LCP("View R Studio")가 빨리 그려지도록
import LoginPage from "./pages/LOGIN";
import AccessRequestPage from "./pages/ACCESS_REQUEST";
import StudioCodeLoginPage from "./pages/StudioCodeLoginPage";
import PairPage from "./pages/PAIR";
import FigmaPluginAuthPage from "./pages/FigmaPluginAuth";
// 코드 스플리팅: 나머지 페이지는 lazy loading
const DashboardPage = lazy(() => import("./pages/HOME"));
const EditorPage = lazy(() => import("./pages/EDITOR"));
const MobileRoutes = lazy(() => import("./mobile/MobileRoutes"));

// React Query 클라이언트 생성 (MEMORY_OPTIMIZATION_DESIGN Phase 3: gcTime 축소)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 기본 쿼리 옵션
      staleTime: 5 * 60 * 1000, // 5분간 fresh 상태 유지
      gcTime: 5 * 60 * 1000, // 5분간 메모리 유지 (Phase 3: 10분→5분으로 축소)
      retry: 3, // 실패 시 3번 재시도
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // 지수 백오프
      refetchOnWindowFocus: true, // 창 포커스 시 자동 리프레시
      refetchOnMount: false, // 마운트 시 리프레시 안 함 (캐시 우선)
      refetchOnReconnect: true, // 네트워크 재연결 시 리프레시
    },
    mutations: {
      // 기본 mutation 옵션
      retry: 1, // mutation은 1번만 재시도
      onError: (error) => {
        // 전역 에러 처리
        console.error("돌연변이 오류:", error);
      },
    },
  },
});

type ThemePreference = "dark" | "light" | "system";

interface ColorModeContextValue {
  toggleColorMode: () => void;
  themePreference: ThemePreference;
  setThemePreference: (preference: ThemePreference) => void;
}

// 다크모드 Context 생성 (선택사항)
export const ColorModeContext = createContext<ColorModeContextValue>({
  toggleColorMode: () => {},
  themePreference: "dark",
  setThemePreference: () => {},
});

interface AuthWrapperProps {
  children: React.ReactNode;
}

// ThemeProvider를 최상위로 이동하여 중첩 제거
// useMediaQuery는 ThemeProvider 내부에서 사용하도록 별도 컴포넌트로 분리
interface ThemeWrapperProps {
  children: React.ReactNode;
  themePreference: ThemePreference;
  setThemePreference: (preference: ThemePreference) => void;
  colorMode: ColorModeContextValue;
}

const ThemeWrapper: React.FC<ThemeWrapperProps> = ({
  children,
  themePreference,
  setThemePreference,
  colorMode,
}) => {
  // useMediaQuery는 ThemeProvider 내부에서 사용
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");

  const effectiveMode =
    themePreference === "system"
      ? prefersDark
        ? "dark"
        : "light"
      : themePreference;
  const theme = useMemo(() => buildTheme(effectiveMode), [effectiveMode]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

const ServerStateEventBridge: React.FC = () => {
  useServerStateEventBridge();
  return null;
};

const App: React.FC = () => {
  // 'dark' | 'light' | 'system'
  const [themePreference, setThemePreference] = useState<ThemePreference>(
    () => {
      return (
        (localStorage.getItem("themePreference") as ThemePreference) || "dark"
      );
    },
  );

  // WebView URL 모니터링 시작
  useEffect(() => {
    const cleanup1 = startUrlMonitoring();
    const cleanup2 = monitorUrlForAuthInfo();

    return () => {
      if (typeof cleanup1 === "function") cleanup1();
      if (typeof cleanup2 === "function") cleanup2();
    };
  }, []);

  // 배포 버전 체크: 새 빌드는 PWA update coordinator에서 안전하게 적용한다.
  useEffect(() => {
    const STORAGE_KEY = "appBuildId";
    const clientBuildId = getClientBuildId();
    publishBuildInfo({ clientBuildId });

    const checkVersion = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        const serverBuildId = data?.buildId ?? "";
        if (!serverBuildId) return;
        const stored = sessionStorage.getItem(STORAGE_KEY);
        publishBuildInfo({ clientBuildId, serverBuildId });
        if (
          shouldRequestPwaUpdate({
            clientBuildId,
            serverBuildId,
            storedBuildId: stored,
          })
        ) {
          requestPwaUpdate({ buildId: serverBuildId });
          return;
        }
        sessionStorage.setItem(STORAGE_KEY, serverBuildId);
      } catch {
        // 네트워크/JSON 오류 시 무시
      }
    };

    const scheduleCheck = () => {
      if (typeof requestIdleCallback !== "undefined") {
        requestIdleCallback(() => checkVersion(), { timeout: 2000 });
      } else {
        setTimeout(checkVersion, 500);
      }
    };
    scheduleCheck();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") checkVersion();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    localStorage.setItem("themePreference", themePreference);
  }, [themePreference]);

  const colorMode = useMemo<ColorModeContextValue>(
    () => ({
      toggleColorMode: () => {
        setThemePreference((prev) => (prev === "light" ? "dark" : "light"));
      },
      themePreference,
      setThemePreference,
    }),
    [themePreference],
  );

  // LoginPage with LogoMotion wrapper (스플래시 단축 → LCP 개선)
  const LoginPageWithSplash: React.FC = () => {
    const hasShownSplash = sessionStorage.getItem("logoMotionShown") === "true";
    const [showSplash, setShowSplash] = useState(!hasShownSplash);

    const handleSplashComplete = () => {
      setShowSplash(false);
      sessionStorage.setItem("logoMotionShown", "true");
    };

    return (
      <>
        {showSplash && <LogoMotion onComplete={handleSplashComplete} />}
        {!showSplash && <LoginPage />}
      </>
    );
  };

  // Guest auth feature가 활성화된 환경에서 GuestAuthProvider 사용
  const AuthWrapperComponent: React.FC<AuthWrapperProps> = React.useMemo(
    () => {
      if (isGuestAuthEnabled()) {
        return ({ children }: AuthWrapperProps) => (
          <GuestAuthProvider>
            <AuthProvider>{children}</AuthProvider>
          </GuestAuthProvider>
        );
      }
      return ({ children }: AuthWrapperProps) => (
        <AuthProvider>{children}</AuthProvider>
      );
    },
    [], // Guest auth 환경값은 빌드 타임에 결정됨
  );

  // Context 구조 최적화: Router를 최상위로 이동하여 불필요한 Context 전파 방지
  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeWrapper
          themePreference={themePreference}
          setThemePreference={setThemePreference}
          colorMode={colorMode}
        >
          <CssBaseline />
          <GlobalBaseStyles />
          <PersistentToastProvider>
          <AppToastProvider>
            <Router>
              <PersistentToastRouteBoundary />
              <AuthWrapperComponent>
                <PwaUpdateCoordinator />
                <ServerStateEventBridge />
                <StudioImportRuntime />
                <ReportIssueHost />
                <Routes>
                  <Route
                    path="/"
                    element={<MobileDeviceRoute desktopPath="/login" />}
                  />
                  <Route
                    path="/login"
                    element={
                      <MobileDeviceRoute>
                        <PublicRoute>
                          <LoginPageWithSplash />
                        </PublicRoute>
                      </MobileDeviceRoute>
                    }
                  />
                  <Route
                    path="/login/studio-code"
                    element={
                      <PairingRoute>
                        <StudioCodeLoginPage />
                      </PairingRoute>
                    }
                  />
                  <Route path="/access" element={<AccessRequestPage />} />
                  <Route
                    path="/pair"
                    element={
                      <PairingRoute>
                        <PairPage />
                      </PairingRoute>
                    }
                  />
                  <Route
                    path="/figma-plugin-auth"
                    element={
                      <PairingRoute>
                        <FigmaPluginAuthPage />
                      </PairingRoute>
                    }
                  />
                  <Route
                    path="/home"
                    element={
                      <MobileDeviceRoute>
                        <ProtectedRoute>
                          <Suspense fallback={<CenterSpinnerMUI />}>
                            <DashboardPage />
                          </Suspense>
                        </ProtectedRoute>
                      </MobileDeviceRoute>
                    }
                  />
                  <Route
                    path="/editor"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<CenterSpinnerMUI />}>
                          <WebWorkerProvider>
                            <EditorPage />
                          </WebWorkerProvider>
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mobile/*"
                    element={
                      <DesktopDeviceRoute>
                        <Suspense fallback={<CenterSpinnerMUI />}>
                          <MobileRoutes />
                        </Suspense>
                      </DesktopDeviceRoute>
                    }
                  />
                </Routes>
              </AuthWrapperComponent>
            </Router>
          </AppToastProvider>
          </PersistentToastProvider>
        </ThemeWrapper>
      </ColorModeContext.Provider>

      {/* 개발 환경에서만 React Query DevTools 표시 */}
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
      <PwaDiagnostics />
    </QueryClientProvider>
  );
};

export default App;
